import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography,
  Box, CircularProgress, Card, CardContent, CardMedia, Button,
  Grid, Divider, Chip, Tooltip, Zoom
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';

const DuplicateResolutionModal = ({ open, onClose, site, date, onResolutionComplete, getSignedUrl, apiUrl, apiKey, analystUrl, analystKey }) => {
  const [loading, setLoading] = useState(true);
  const [duplicates, setDuplicates] = useState([]);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (open && site && date) {
      fetchDuplicates();
    }
  }, [open, site, date]);

  const fetchDuplicates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${analystUrl}/audit/duplicate-intelligence`, {
        params: {
          websitesDbId: site.WebsitesDBID,
          targetDate: moment(date).format('YYYY-MM-DD')
        }
      });
      
      const duplicatesData = response.data.duplicates || [];
      if (getSignedUrl) {
        for (let dup of duplicatesData) {
          if (dup.imageLocation) {
            try {
              dup.signedImageUrl = await getSignedUrl(dup.imageLocation);
            } catch (e) {
              console.error('Failed to sign URL for image', dup.imageLocation, e);
            }
          }
        }
      }
      setDuplicates(duplicatesData);
    } catch (err) {
      console.error('Failed to fetch duplicates:', err);
      setError('Could not load duplicate intelligence. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scanId) => {
    if (!window.confirm("Are you sure you want to permanently delete this scan and all its confirmed analysis?")) {
      return;
    }
    
    setDeletingId(scanId);
    try {
      // 1. Delete UnconfirmedAnalysis from Brazilia
      await axios.delete(`${analystUrl}/unconfirmed-analysis/bulk/${scanId}`, {
        headers: { 'x-api-key': analystKey }
      });
      
      // 2. Delete WebsitesData (and S3 image) from Rio
      await axios.delete(`${apiUrl}/audit/${scanId}`, {
        headers: { 'x-api-key': apiKey }
      });
      
      // Refresh list
      setDuplicates(prev => prev.filter(d => d.scanId !== scanId));
      
      // If we only have 1 left (or 0), we can close and refresh the main grid
      if (duplicates.length <= 2) {
        onResolutionComplete();
        onClose();
      }
    } catch (err) {
      console.error('Failed to delete duplicate:', err);
      alert('Failed to delete the duplicate scan. See console for details.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary">
            Resolve Duplicate Scans
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {site?.WebsiteName} • {moment(date).format('MMMM Do YYYY')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, minHeight: '400px' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : error ? (
          <Box textAlign="center" color="error.main" mt={4}>
            <WarningIcon sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
            <Typography variant="h6">{error}</Typography>
          </Box>
        ) : duplicates.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <Typography variant="h6" color="text.secondary">No duplicates found for this date.</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {duplicates.map((dup, idx) => (
              <Zoom in={true} style={{ transitionDelay: `${idx * 100}ms` }} key={dup.scanId}>
                <Grid item xs={12} md={6} lg={duplicates.length > 2 ? 4 : 6}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: dup.checked ? 'success.light' : 'divider',
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.08)'
                      }
                    }}
                  >
                    <Box position="relative">
                      <CardMedia
                        component="img"
                        height="200"
                        image={dup.signedImageUrl || dup.imageLocation}
                        alt="Screenshot"
                        sx={{ objectFit: 'cover', objectPosition: 'top', bgcolor: 'grey.100' }}
                      />
                      <Box 
                        position="absolute" 
                        top={8} 
                        right={8} 
                        display="flex" 
                        gap={1}
                      >
                        {dup.checked && (
                          <Chip size="small" icon={<CheckCircleIcon />} label="Checked" color="success" sx={{ backdropFilter: 'blur(4px)' }} />
                        )}
                        <Chip 
                          size="small" 
                          icon={<ScheduleIcon />} 
                          label={`${moment.utc(dup.timestamp).format('HH:mm')} UTC`} 
                          sx={{ bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 'bold' }} 
                        />
                      </Box>
                      <Box 
                        position="absolute" 
                        bottom={8} 
                        left={8}
                      >
                        <Chip 
                          size="small" 
                          label={dup.screenshotStatus} 
                          color={dup.screenshotStatus === 'SUCCESS' ? 'primary' : 'error'} 
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Grid container spacing={2} mb={2}>
                        <Grid item xs={6}>
                          <Box textAlign="center" p={1.5} bgcolor="primary.50" borderRadius={2}>
                            <Typography variant="h4" color="primary.main" fontWeight="bold">{dup.autoConfirmedGames}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Auto Confirmed</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box textAlign="center" p={1.5} bgcolor="secondary.50" borderRadius={2}>
                            <Typography variant="h4" color="secondary.main" fontWeight="bold">{dup.manualConfirmedGames}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Manual Confirmed</Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={4}>
                          <Box textAlign="center" p={1} border="1px solid" borderColor="divider" borderRadius={1.5}>
                            <Typography variant="h6" fontWeight="bold">{dup.unassignedTiles}</Typography>
                            <Typography variant="caption" color="text.secondary">Unconfirmed</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box textAlign="center" p={1} border="1px solid" borderColor="divider" borderRadius={1.5}>
                            <Typography variant="h6" fontWeight="bold">{dup.gameGroupCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Game Groups</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box textAlign="center" p={1} border="1px solid" borderColor="divider" borderRadius={1.5}>
                            <Typography variant="h6" fontWeight="bold">{dup.promotionCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Promotions</Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          ID: {dup.scanId.split('-')[0]}...
                        </Typography>
                        <Tooltip title="Permanently delete this scan and its tile data">
                          <span>
                            <Button 
                              variant="outlined" 
                              color="error" 
                              size="small"
                              startIcon={deletingId === dup.scanId ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                              onClick={() => handleDelete(dup.scanId)}
                              disabled={deletingId !== null}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                            >
                              {deletingId === dup.scanId ? 'Deleting...' : 'Delete'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateResolutionModal;
